// ═══════════════════════════════════════════════════════════════
// MEMBER DECK VIEW — Card-by-card focused member review
// Navigate through filtered members one at a time
// Path: src/components/donors/DonorDeckView.jsx
//
// Enhanced: Feb 2026
// - Added: end date, director, log contact button, last event
// - Applied: Critical UI Color Settings (all 6 rules)
// - Swipe + arrows + keyboard navigation
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Building2,
  Calendar,
  User,
  UserCheck,
  AlertCircle,
  Pencil,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import {
  calculateEngagement,
  getPathwayForScore,
  getMemberActivities,
} from "../../utils/engagementScoring";
import {
  getLastContactLog,
  CONTACT_TYPE_ICONS,
} from "../../utils/cultivationUtils";
import { QuickLogModal } from "../features/QuickLogModal";

// ── Format a date for display ─────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

// ── Pathway badge colors ──────────────────────────────────────
const PATHWAY_BADGE = {
  cold: { bg: "#6b7280", label: "Cold" },
  connect: { bg: "#d97706", label: "Connect" },
  contribute: { bg: "#059669", label: "Contribute" },
  lead: { bg: "#7c3aed", label: "Lead" },
  prospect: { bg: "#ea580c", label: "Prospect" },
};

// ── Days since contact color ──────────────────────────────────
const getDaysColor = (days) => {
  if (days === null) return "#dc2626";
  if (days <= 14) return "#16a34a";
  if (days <= 30) return "#65a30d";
  if (days <= 60) return "#d97706";
  return "#dc2626";
};

// ── Find last event attended from contact log ─────────────────
const EVENT_TYPES = ["event", "luncheon", "workshop", "conference", "meeting"];
const getLastEventAttended = (member) => {
  const logs = member?.contactLog || [];
  for (const log of logs) {
    // Contact logs are newest-first
    const logType = (log.type || "").toLowerCase();
    if (EVENT_TYPES.includes(logType)) {
      return {
        type: log.type,
        date: log.date || log.createdAt,
        subject: log.subject || log.notes || null,
      };
    }
    // Also check if subject/notes mention an event
    if (
      log.subject &&
      /event|luncheon|workshop|conference/i.test(log.subject)
    ) {
      return {
        type: log.type,
        date: log.date || log.createdAt,
        subject: log.subject,
      };
    }
  }
  return null;
};

// ── Membership end date status ────────────────────────────────
const getEndDateStatus = (endDate) => {
  if (!endDate)
    return { label: "No end date", color: "#6b7280", urgent: false };
  const d = new Date(endDate);
  if (isNaN(d.getTime()))
    return { label: "Invalid date", color: "#6b7280", urgent: false };
  const now = new Date();
  const diffDays = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  if (diffDays < 0)
    return {
      label: `Expired ${formatDate(endDate)}`,
      color: "#dc2626",
      urgent: true,
    };
  if (diffDays <= 30)
    return {
      label: `Expires ${formatDate(endDate)}`,
      color: "#d97706",
      urgent: true,
    };
  if (diffDays <= 90)
    return {
      label: `Expires ${formatDate(endDate)}`,
      color: "#65a30d",
      urgent: false,
    };
  return {
    label: `Expires ${formatDate(endDate)}`,
    color: "#6b7280",
    urgent: false,
  };
};

export const DonorDeckView = ({ members, onMemberClick }) => {
  const { palette, pathways, personas, allGifts, allEventAttendance, allSponsorships } = useApp();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const containerRef = useRef(null);
  const touchStartRef = useRef(null);

  // Clamp index when member list changes
  useEffect(() => {
    if (currentIndex >= members.length) {
      setCurrentIndex(Math.max(0, members.length - 1));
    }
  }, [members.length, currentIndex]);

  const member = members[currentIndex] || null;

  // ── Navigation ────────────────────────────────────────────
  const goTo = useCallback(
    (direction) => {
      if (isAnimating || members.length <= 1) return;
      const nextIndex =
        direction === "next"
          ? (currentIndex + 1) % members.length
          : (currentIndex - 1 + members.length) % members.length;

      setSlideDirection(direction === "next" ? "left" : "right");
      setIsAnimating(true);

      setTimeout(() => {
        setCurrentIndex(nextIndex);
        setSlideDirection(null);
        setIsAnimating(false);
      }, 200);
    },
    [currentIndex, members.length, isAnimating]
  );

  const goNext = useCallback(() => goTo("next"), [goTo]);
  const goPrev = useCallback(() => goTo("prev"), [goTo]);

  // ── Keyboard navigation ───────────────────────────────────
  useEffect(() => {
    const handleKey = (e) => {
      if (showLog) return; // Don't navigate while modal is open
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "Enter" && member) {
        onMemberClick?.(member);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goNext, goPrev, member, onMemberClick, showLog]);

  // ── Touch/Swipe ───────────────────────────────────────────
  const handleTouchStart = (e) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  };

  const handleTouchEnd = (e) => {
    if (!touchStartRef.current) return;
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
    const dy = e.changedTouches[0].clientY - touchStartRef.current.y;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) goNext();
      else goPrev();
    }
    touchStartRef.current = null;
  };

  // ── Empty state ───────────────────────────────────────────
  if (members.length === 0) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "80px 20px",
          fontFamily: FONT_FAMILY,
        }}
      >
        <User
          size={ICON_SIZES.xxl}
          style={{ color: palette.text2, opacity: 0.4, marginBottom: 16 }}
        />
        <div
          style={{
            fontSize: FONT_SIZES.md,
            fontWeight: 600,
            color: palette.text1,
          }}
        >
          No members to display
        </div>
        <div
          style={{
            fontSize: FONT_SIZES.sm,
            marginTop: 4,
            color: palette.text2,
          }}
        >
          Adjust your search or filters
        </div>
      </div>
    );
  }

  // ── Compute member data ───────────────────────────────────
  const engagement = member ? calculateEngagement(member, undefined, getMemberActivities(member.id, allGifts, allEventAttendance, allSponsorships)) : null;
  const pathwayInfo =
    member && engagement ? getPathwayForScore(engagement.score) : null;
  const lastLog = member ? getLastContactLog(member) : null;
  const lastEvent = member ? getLastEventAttended(member) : null;
  const endDateStatus = member ? getEndDateStatus(member.endDate) : null;
  const memberPersona =
    member?.persona && (personas || []).length > 0
      ? personas.find((p) => p.id === member.persona) || null
      : null;

  const effectivePathway =
    member?.pathway === "prospect" ? "prospect" : pathwayInfo?.key || "cold";
  const pwBadge = PATHWAY_BADGE[effectivePathway] || PATHWAY_BADGE.cold;

  const scoreColor =
    engagement?.score >= 80
      ? "#10b981"
      : engagement?.score >= 30
      ? "#3b82f6"
      : engagement?.score >= 10
      ? "#f59e0b"
      : "#ef4444";

  const daysColor = getDaysColor(engagement?.daysSinceContact);

  const initials = (
    (member?.firstName || "?")[0] + (member?.lastName || "?")[0]
  ).toUpperCase();

  // ── Styles (Critical UI Color Settings compliant) ─────────
  const s = {
    // RULE 4: badges use solid bg + white text
    badge: (bg) => ({
      fontSize: FONT_SIZES.xs,
      fontWeight: 700,
      padding: "3px 10px",
      borderRadius: 999,
      background: bg,
      color: "#ffffff",
      whiteSpace: "nowrap",
    }),
    // RULE 6: labels = text1 + bold
    label: {
      fontSize: FONT_SIZES.xs,
      fontWeight: 700,
      color: palette.text1,
      textTransform: "uppercase",
      letterSpacing: "0.03em",
    },
    // RULE 1: readable value text = text2
    value: {
      fontSize: FONT_SIZES.sm,
      color: palette.text2,
      lineHeight: 1.4,
    },
    // Info row container
    infoRow: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 0",
    },
  };

  return (
    <div
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        flex: 1,
        padding: "24px 24px",
        fontFamily: FONT_FAMILY,
        userSelect: "none",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {/* ── Counter / Progress ─────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <span
          style={{
            fontSize: FONT_SIZES.sm,
            color: palette.textOnGrad,
            fontWeight: 600,
          }}
        >
          {currentIndex + 1} of {members.length}
        </span>
        {members.length <= 20 && (
          <div style={{ display: "flex", gap: 4 }}>
            {members.map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  if (i !== currentIndex) {
                    setSlideDirection(i > currentIndex ? "left" : "right");
                    setIsAnimating(true);
                    setTimeout(() => {
                      setCurrentIndex(i);
                      setSlideDirection(null);
                      setIsAnimating(false);
                    }, 200);
                  }
                }}
                style={{
                  width: i === currentIndex ? 18 : 8,
                  height: 8,
                  borderRadius: 4,
                  border: "none",
                  background:
                    i === currentIndex ? "#ffffff" : "rgba(255,255,255,0.35)",
                  cursor: "pointer",
                  padding: 0,
                  transition: "all 0.2s ease",
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Card + Arrows Row ──────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 16,
          maxWidth: 560,
          width: "100%",
        }}
      >
        {/* Prev Arrow */}
        <button
          onClick={goPrev}
          disabled={members.length <= 1}
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            border: `1px solid rgba(255,255,255,0.3)`,
            background: "rgba(255,255,255,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: members.length > 1 ? "pointer" : "default",
            opacity: members.length > 1 ? 1 : 0.3,
            transition: "all 0.15s",
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            if (members.length > 1)
              e.currentTarget.style.background = "rgba(255,255,255,0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.15)";
          }}
        >
          <ChevronLeft size={ICON_SIZES.md} style={{ color: "#ffffff" }} />
        </button>

        {/* ── THE CARD ─────────────────────────────────────── */}
        <div
          onClick={() => member && onMemberClick?.(member)}
          style={{
            flex: 1,
            background: palette.cardBg, // RULE 2: solid background
            borderRadius: 16,
            border: `1px solid ${palette.cardBorder}`,
            boxShadow:
              "0 8px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.05)",
            cursor: "pointer",
            overflow: "hidden",
            transition: "transform 0.2s ease, opacity 0.2s ease",
            transform:
              slideDirection === "left"
                ? "translateX(-30px)"
                : slideDirection === "right"
                ? "translateX(30px)"
                : "translateX(0)",
            opacity: isAnimating ? 0 : 1,
          }}
        >
          {/* ── Card Header: Color bar ── */}
          <div
            style={{
              height: 6,
              background: `linear-gradient(90deg, ${pwBadge.bg}, ${scoreColor})`,
            }}
          />

          {/* ── Card Body ── */}
          <div style={{ padding: "20px 24px 16px" }}>
            {/* Top row: Avatar + Name/Org */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 16,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: 14,
                  background: `linear-gradient(135deg, ${palette.accent1}, ${
                    palette.accent2 || palette.accent1
                  })`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                  fontWeight: 800,
                  color: "#fff",
                  flexShrink: 0,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  letterSpacing: 1,
                }}
              >
                {initials}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: FONT_SIZES.xl,
                    fontWeight: 700,
                    color: palette.text1, // RULE 1
                    lineHeight: 1.2,
                  }}
                >
                  {member?.firstName} {member?.lastName}
                </div>
                {member?.credentials && (
                  <div
                    style={{
                      fontSize: FONT_SIZES.sm,
                      color: palette.text2, // RULE 1: not accent
                      fontWeight: 600,
                      marginTop: 2,
                    }}
                  >
                    {member.credentials}
                  </div>
                )}
                {member?.organization && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                      fontSize: FONT_SIZES.sm,
                      color: palette.text2,
                      marginTop: 4,
                    }}
                  >
                    <Building2
                      size={ICON_SIZES.sm}
                      style={{ flexShrink: 0, color: palette.text2 }}
                    />
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {member.organization}
                    </span>
                  </div>
                )}
                {member?.title && (
                  <div
                    style={{
                      fontSize: FONT_SIZES.xs,
                      color: palette.text2, // RULE 1: was text3
                      marginTop: 2,
                    }}
                  >
                    {member.title}
                  </div>
                )}
              </div>

              {/* Log Contact Button — top right of card */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowLog(true);
                }}
                title="Log contact"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "6px 12px",
                  borderRadius: 8,
                  border: "none",
                  background: palette.accent || palette.accent1,
                  color: "#ffffff", // RULE 3: white on accent bg
                  fontSize: FONT_SIZES.xs,
                  fontWeight: 600,
                  fontFamily: FONT_FAMILY,
                  cursor: "pointer",
                  flexShrink: 0,
                  transition: "opacity 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.85")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                <Pencil size={ICON_SIZES.xs} />
                Log
              </button>
            </div>

            {/* ── Badges Row: Pathway + Persona + Flags ── */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginBottom: 16,
              }}
            >
              {/* RULE 4: solid bg + white text for all badges */}
              <span style={s.badge(pwBadge.bg)}>{pwBadge.label}</span>
              {memberPersona && (
                <span style={s.badge(memberPersona.color || "#6b7280")}>
                  {memberPersona.name}
                </span>
              )}
              {member?.isBoardMember && (
                <span style={s.badge("#1e40af")}>Board</span>
              )}
              {member?.isDonor && <span style={s.badge("#166534")}>Donor</span>}
            </div>

            {/* ── Stats Row: Score + Days + Last Contact ── */}
            <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
              {/* Engagement Score */}
              <div
                style={{
                  flex: 1,
                  textAlign: "center",
                  padding: "12px 8px",
                  borderRadius: 10,
                  background: palette.cardBg,
                  border: `2px solid ${scoreColor}30`,
                }}
              >
                <div
                  style={{
                    fontSize: FONT_SIZES.xxl,
                    fontWeight: 800,
                    color: scoreColor,
                    lineHeight: 1,
                  }}
                >
                  {engagement?.score ?? "—"}
                </div>
                <div style={{ ...s.label, marginTop: 4 }}>Engagement</div>
              </div>

              {/* Days Since Contact */}
              <div
                style={{
                  flex: 1,
                  textAlign: "center",
                  padding: "12px 8px",
                  borderRadius: 10,
                  background: palette.cardBg,
                  border: `2px solid ${daysColor}30`,
                }}
              >
                <div
                  style={{
                    fontSize: FONT_SIZES.xxl,
                    fontWeight: 800,
                    color: daysColor,
                    lineHeight: 1,
                  }}
                >
                  {engagement?.daysSinceContact === null
                    ? "—"
                    : engagement?.daysSinceContact}
                </div>
                <div style={{ ...s.label, marginTop: 4 }}>
                  {engagement?.daysSinceContact === null
                    ? "No Contact"
                    : "Days Ago"}
                </div>
              </div>

              {/* Last Contact Type */}
              <div
                style={{
                  flex: 1,
                  textAlign: "center",
                  padding: "12px 8px",
                  borderRadius: 10,
                  background: palette.cardBg,
                  border: `2px solid ${palette.cardBorder}`,
                }}
              >
                {lastLog ? (
                  <>
                    <div
                      style={{ fontSize: 22, lineHeight: 1, marginBottom: 4 }}
                    >
                      {CONTACT_TYPE_ICONS[lastLog.type] || "📝"}
                    </div>
                    <div
                      style={{
                        fontSize: FONT_SIZES.xs,
                        fontWeight: 600,
                        color: palette.text1,
                        textTransform: "capitalize",
                      }}
                    >
                      {lastLog.type}
                    </div>
                    <div
                      style={{
                        fontSize: FONT_SIZES.xs,
                        color: palette.text2,
                        marginTop: 1,
                      }}
                    >
                      {formatDate(lastLog.date || lastLog.createdAt)}
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        fontSize: 22,
                        lineHeight: 1,
                        marginBottom: 4,
                        opacity: 0.3,
                      }}
                    >
                      📝
                    </div>
                    <div
                      style={{
                        fontSize: FONT_SIZES.xs,
                        fontWeight: 600,
                        color: palette.text2,
                      }}
                    >
                      No contact
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ── Info Grid: Director, End Date, Last Event ── */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "4px 16px",
                padding: "12px 14px",
                borderRadius: 10,
                background: palette.cardBg,
                border: `1px solid ${palette.cardBorder}`,
              }}
            >
              {/* Director */}
              <div style={s.infoRow}>
                <UserCheck
                  size={ICON_SIZES.sm}
                  style={{ color: palette.text2, flexShrink: 0 }}
                />
                <div>
                  <div style={s.label}>Director</div>
                  <div style={s.value}>
                    {member?.assignedDirector?.trim() || (
                      <span style={{ color: "#d97706", fontWeight: 600 }}>
                        Unassigned
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* End Date */}
              <div style={s.infoRow}>
                <Calendar
                  size={ICON_SIZES.sm}
                  style={{
                    color: endDateStatus?.urgent
                      ? endDateStatus.color
                      : palette.text2,
                    flexShrink: 0,
                  }}
                />
                <div>
                  <div style={s.label}>Membership</div>
                  <div
                    style={{
                      ...s.value,
                      color: endDateStatus?.urgent
                        ? endDateStatus.color
                        : palette.text2,
                      fontWeight: endDateStatus?.urgent ? 700 : 400,
                    }}
                  >
                    {endDateStatus?.label || "No end date"}
                  </div>
                </div>
              </div>

              {/* Last Event */}
              <div style={{ ...s.infoRow, gridColumn: "1 / -1" }}>
                <Calendar
                  size={ICON_SIZES.sm}
                  style={{ color: palette.text2, flexShrink: 0 }}
                />
                <div>
                  <div style={s.label}>Last Event</div>
                  <div style={s.value}>
                    {lastEvent ? (
                      <>
                        {lastEvent.subject && (
                          <span style={{ fontWeight: 600 }}>
                            {lastEvent.subject} ·{" "}
                          </span>
                        )}
                        <span style={{ textTransform: "capitalize" }}>
                          {lastEvent.type}
                        </span>
                        {" — "}
                        {formatDate(lastEvent.date)}
                      </>
                    ) : (
                      "No events on record"
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Persona Cultivation Hint ── */}
            {memberPersona?.actionHint && (
              <div
                style={{
                  marginTop: 12,
                  padding: "10px 12px",
                  borderRadius: 8,
                  background: palette.cardBg,
                  border: `1px solid ${palette.cardBorder}`,
                  borderLeft: `3px solid ${memberPersona.color}`,
                }}
              >
                <div style={{ ...s.label, marginBottom: 2 }}>
                  Cultivation Guidance
                </div>
                <div
                  style={{
                    fontSize: FONT_SIZES.sm,
                    color: palette.text2,
                    lineHeight: 1.5,
                    fontStyle: "italic",
                  }}
                >
                  {memberPersona.actionHint.length > 180
                    ? memberPersona.actionHint.substring(0, 180) + "…"
                    : memberPersona.actionHint}
                </div>
              </div>
            )}
          </div>

          {/* ── Card Footer ── */}
          <div
            style={{
              padding: "10px 24px",
              borderTop: `1px solid ${palette.cardBorder}`,
              textAlign: "center",
              fontSize: FONT_SIZES.xs,
              color: palette.text2, // RULE 1: was text3
              fontWeight: 500,
            }}
          >
            Tap card to open full profile · Swipe or use ← → to navigate
          </div>
        </div>

        {/* Next Arrow */}
        <button
          onClick={goNext}
          disabled={members.length <= 1}
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            border: `1px solid rgba(255,255,255,0.3)`,
            background: "rgba(255,255,255,0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: members.length > 1 ? "pointer" : "default",
            opacity: members.length > 1 ? 1 : 0.3,
            transition: "all 0.15s",
            flexShrink: 0,
          }}
          onMouseEnter={(e) => {
            if (members.length > 1)
              e.currentTarget.style.background = "rgba(255,255,255,0.3)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.15)";
          }}
        >
          <ChevronRight size={ICON_SIZES.md} style={{ color: "#ffffff" }} />
        </button>
      </div>

      {/* ── Keyboard hint ──────────────────────────────────── */}
      <div
        style={{
          marginTop: 16,
          fontSize: FONT_SIZES.xs,
          color: palette.textOnGrad,
          opacity: 0.7,
          display: "flex",
          gap: 16,
          alignItems: "center",
        }}
      >
        <span>
          <kbd
            style={{
              padding: "2px 6px",
              borderRadius: 4,
              border: "1px solid rgba(255,255,255,0.3)",
              background: "rgba(255,255,255,0.15)",
              fontSize: FONT_SIZES.xs,
              fontFamily: FONT_FAMILY,
              color: palette.textOnGrad,
            }}
          >
            ←
          </kbd>{" "}
          Prev
        </span>
        <span>
          <kbd
            style={{
              padding: "2px 6px",
              borderRadius: 4,
              border: "1px solid rgba(255,255,255,0.3)",
              background: "rgba(255,255,255,0.15)",
              fontSize: FONT_SIZES.xs,
              fontFamily: FONT_FAMILY,
              color: palette.textOnGrad,
            }}
          >
            →
          </kbd>{" "}
          Next
        </span>
        <span>
          <kbd
            style={{
              padding: "2px 6px",
              borderRadius: 4,
              border: "1px solid rgba(255,255,255,0.3)",
              background: "rgba(255,255,255,0.15)",
              fontSize: FONT_SIZES.xs,
              fontFamily: FONT_FAMILY,
              color: palette.textOnGrad,
            }}
          >
            Enter
          </kbd>{" "}
          Open
        </span>
        <span>Swipe on mobile</span>
      </div>

      {/* ── QuickLog Modal ─────────────────────────────────── */}
      {showLog && member && (
        <QuickLogModal member={member} onClose={() => setShowLog(false)} />
      )}
    </div>
  );
};
